import { BreadcrumbNavigation } from "@/components/molecules/BreadcrumbNavigation";
import { GoogleAnalyticsSection } from "@/components/organisms/GoogleAnalyticsSection";
import { GoogleSearchConsoleSection } from "@/components/organisms/GoogleSearchConsoleSection";
import { OfferImportForm } from "@/components/organisms/OfferImportForm";
import { OfferSection } from "@/components/organisms/OfferSection";
import { PersonaImportForm } from "@/components/organisms/PersonaImportForm";
import { PersonaSection } from "@/components/organisms/PersonaSection";
import { PolicyForm } from "@/components/organisms/PolicyForm";
import { PolicySection } from "@/components/organisms/PolicySection";
import { WordPressSection } from "@/components/organisms/WordPressSection";
import { Separator } from "@/components/ui/separator";
import { Offer } from "@/schemas/firestore/offer.schema";
import { Persona } from "@/schemas/firestore/persona.schema";
import { Policy } from "@/schemas/firestore/policy.schema";

export interface ContextPanelContentProps {
  showPersonaImportForm: boolean;
  showOfferImportForm: boolean;
  showPolicyForm: boolean;
  /** Only policies are editable; personas and offers are AI-generated only. */
  editingItem: { id: string; type: "policy" } | null;
  personas: Persona[];
  offers: Offer[];
  policies: Policy[];
  selectedPersonas: string[];
  selectedOffers: string[];
  selectedPolicies: string[];
  isLoadingPersonas: boolean;
  isGeneratingPersonas: boolean;
  isLoadingOffers: boolean;
  isGeneratingOffers: boolean;
  isLoadingPolicies: boolean;
  isImportingPersona: boolean;
  isImportingOffer: boolean;
  onSavePolicy: (
    policyData: Omit<
      Policy,
      "id" | "created_at" | "updated_at" | "created_by" | "updated_by" | "domain_id"
    >,
  ) => void;
  onBackToContext: () => void;
  onImportPersona: () => void;
  onImportPersonaSubmit: (description: string) => Promise<void>;
  onImportOffer: () => void;
  onImportOfferSubmit: (description: string) => Promise<void>;
  onAddPolicy: () => void;
  onEditPolicy: (id: string) => void;
  onDeletePolicy: (id: string) => void;
  onTogglePersona: (id: string) => void;
  onToggleOffer: (id: string) => void;
  onTogglePolicy: (id: string) => void;
  onGeneratePersonas: () => void;
  onGenerateOffer: () => void;
}

/**
 * Context Panel Content organism that handles the main content area
 * of the context sidebar, including personas, offers, and policies sections,
 * as well as form views for editing these items.
 */
export function ContextPanelContent({
  showPersonaImportForm,
  showOfferImportForm,
  showPolicyForm,
  editingItem,
  personas,
  offers,
  policies,
  selectedPersonas,
  selectedOffers,
  selectedPolicies,
  isLoadingPersonas,
  isGeneratingPersonas,
  isLoadingOffers,
  isGeneratingOffers,
  isLoadingPolicies,
  isImportingPersona,
  isImportingOffer,
  onSavePolicy,
  onBackToContext,
  onImportPersona,
  onImportPersonaSubmit,
  onImportOffer,
  onImportOfferSubmit,
  onAddPolicy,
  onEditPolicy,
  onDeletePolicy,
  onTogglePersona,
  onToggleOffer,
  onTogglePolicy,
  onGeneratePersonas,
  onGenerateOffer,
}: ContextPanelContentProps) {
  return (
    <>
      {showPersonaImportForm || showOfferImportForm || showPolicyForm ? (
        <>
          <BreadcrumbNavigation
            showPersonaImportForm={showPersonaImportForm}
            showOfferImportForm={showOfferImportForm}
            showPolicyForm={showPolicyForm}
            isEditing={!!editingItem}
            onBackToContext={onBackToContext}
          />

          {/* Form Component */}
          {showPersonaImportForm ? (
            <PersonaImportForm
              onSubmit={onImportPersonaSubmit}
              onCancel={onBackToContext}
              isSubmitting={isImportingPersona}
            />
          ) : showOfferImportForm ? (
            <OfferImportForm
              onSubmit={onImportOfferSubmit}
              onCancel={onBackToContext}
              isSubmitting={isImportingOffer}
            />
          ) : (
            <PolicyForm
              policy={
                editingItem
                  ? policies.find((p) => p.id === editingItem.id)
                  : undefined
              }
              isEditing={!!editingItem}
              onSave={onSavePolicy}
              onCancel={onBackToContext}
            />
          )}
        </>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Add, Edit & Select Context
            </h2>
          </div>

          {/* Personas Section */}
          <PersonaSection
            personas={personas}
            selectedPersonas={selectedPersonas}
            isLoadingPersonas={isLoadingPersonas}
            isGeneratingPersonas={isGeneratingPersonas}
            onImportPersona={onImportPersona}
            onTogglePersona={onTogglePersona}
            onGeneratePersonas={onGeneratePersonas}
          />

          <Separator className="my-6" />

          {/* Offers Section */}
          <OfferSection
            offers={offers}
            selectedOffers={selectedOffers}
            isLoadingOffers={isLoadingOffers}
            isGeneratingOffers={isGeneratingOffers}
            onImportOffer={onImportOffer}
            onToggleOffer={onToggleOffer}
            onGenerateOffer={onGenerateOffer}
          />

          <Separator className="my-6" />

          {/* Google Analytics Section */}
          <GoogleAnalyticsSection />

          <Separator className="my-6" />

          {/* Google Search Console Section */}
          <GoogleSearchConsoleSection />

          <Separator className="my-6" />

          {/* WordPress Section */}
          <WordPressSection />

          <Separator className="my-6" />

          {/* Policies Section */}
          <PolicySection
            policies={policies}
            selectedPolicies={selectedPolicies}
            isLoadingPolicies={isLoadingPolicies}
            onAddPolicy={onAddPolicy}
            onEditPolicy={onEditPolicy}
            onDeletePolicy={onDeletePolicy}
            onTogglePolicy={onTogglePolicy}
          />
        </>
      )}
    </>
  );
}
